import { DataSourceType } from "@coeff/api";
import { AdminExtWritebackRun, DefaultApiGetWritebackRunsRequest } from "@coeff/api-admin-ext";
import { useQuery } from "@tanstack/react-query";
import { Menu } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey, SortColumn, SortDirection } from "../../../api";
import {
  Button,
  ChevronLeft,
  FlexColumn,
  FlexRow,
  GoogleSheet,
  ImportTypeIcon,
  LoaderWithPerfTimings,
  SortDropdown,
  Tooltip,
  Typography,
} from "../../../components";
import { AdminTable } from "../../../components/AdminTable";
import { StatusTag } from "../../../components/StatusTag";
import { COLORS } from "../../../constants";
import { capitalize, getTimeZone, useApiContext } from "../../../utils";
import { useTableFilters } from "../useTableFilters";

import { getSortIcon } from "./DataImports";
import { IconDropdown } from "./IconDropdown";
import { WritebackRunsFilterToolbar } from "./WritebackRunsFilterToolbar";

const Container = styled.div`
  height: 100%;
  width: 100%;

  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
  overflow: hidden;
  overflow-x: auto;
`;

const PaginationButton = styled(Button)`
  &&.ant-btn {
    border-radius: 0;
    padding: 14px 8px;
    align-items: center;
    font-size: 12px;

    &:active,
    &:focus,
    &:hover {
      border-color: ${COLORS.coeblue4};
      background-color: transparent;
    }
  }
`;

export const WritebackRuns: React.FC = () => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const domain = searchParams.get("domain") ?? "all_domains";

  const { adminExtApiClient, apiClient } = useApiContext();

  const [search, setSearch] = useState("");

  const [sortedColumn, setSortedColumn] = useState<SortColumn>({
    columnKey: "created_dt",
    value: SortDirection.descend,
  });

  const [page, setPage] = React.useState(0);

  const pageSize = 100;

  const [pageOffset, setPageOffset] = useState<number>(0);

  const setPageAndOffset = (page: number) => {
    setPage(page);
    setPageOffset(page * pageSize);
  };

  const downloadRunHistory = async (writebackRunId: string) => {
    const { data, headers } = await apiClient.getWritebackRowStatuses(
      { writeback_run_id: writebackRunId },
      { responseType: "blob" }
    );

    const filename =
      headers["content-disposition"]?.split(";")[1]?.split("=")[1]?.replace(/"/g, "") ??
      `writeback_run_${writebackRunId}.csv`;

    const url = window.URL.createObjectURL(new Blob([data as BlobPart]));

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const { filters, setFilters } = useTableFilters<keyof DefaultApiGetWritebackRunsRequest>([
    "writeback_id",
    "user_id",
    "data_source_type",
    "status",
    "trigger",
  ]);

  const onFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setPage(0);
    setPageOffset(0);
  };

  const { data: writebackRunsResponse, isLoading } = useQuery(
    [
      QueryCacheKey.ADMIN_EXT_WRITEBACK_RUNS,
      domain,
      pageSize,
      pageOffset,
      sortedColumn,
      search,
      filters,
    ],
    () =>
      adminExtApiClient.getWritebackRuns({
        domain,
        page_size: pageSize,
        offset: pageOffset,
        sort_by: sortedColumn.columnKey,
        sort_order: sortedColumn.value,
        tz_name: getTimeZone(),
        search,
        writeback_id: filters.writeback_id?.[0],
        user_id: filters.user_id as unknown as string,
        data_source_type: filters.data_source_type as unknown as string,
        status: filters.status as unknown as string,
        trigger: filters.trigger as unknown as string,
      }),
    {
      enabled: Boolean(domain),
    }
  );

  const writebackRuns = writebackRunsResponse?.data.writeback_runs ?? [];
  const hasMore = writebackRunsResponse?.data.has_more ?? false;

  const columns: ColumnsType<AdminExtWritebackRun> = [
    {
      title: <Typography noWrap>Export Name</Typography>,
      dataIndex: "writeback_name",
      key: "writeback_name",
      showSorterTooltip: false,
      width: 300,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="writeback_name"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "writeback_name"),
      render: (writebackName: string, run) => {
        return (
          <FlexRow style={{ maxWidth: 300 }}>
            <GoogleSheet height="24px" width="24px" />

            <FlexColumn gap={0} minWidthZero>
              <Typography title={writebackName} noWrap fontWeight={500}>
                {writebackName}
              </Typography>

              <Typography
                title={run.google_sheets_file_name}
                noWrap
                color="textSecondary"
                underline={Boolean(run.spreadsheet_url)}
                onClick={e => {
                  e.stopPropagation();
                  run.spreadsheet_url ? window.open(run.spreadsheet_url, "_blank") : null;
                }}
              >
                {run.google_sheets_file_name || "-"}
              </Typography>
            </FlexColumn>
          </FlexRow>
        );
      },
    },
    {
      title: <Typography noWrap>Destination</Typography>,
      dataIndex: "data_source_type",
      key: "data_source_type",
      showSorterTooltip: false,
      width: 200,
      filterDropdown: ({ confirm }) => (
        <SortDropdown
          confirm={confirm}
          columnKey="data_source_type"
          setSortedColumn={setSortedColumn}
        />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "data_source_type"),
      render: (_: string, run) => {
        return (
          <div>
            <FlexRow>
              <ImportTypeIcon size="small" type={run.data_source_type as DataSourceType} />

              <Typography noWrap>{run.data_source_name}</Typography>

              {run.object_type_name ? (
                <span
                  style={{
                    backgroundColor: "#CDEEF3",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {run.object_type_name}
                </span>
              ) : null}
            </FlexRow>
          </div>
        );
      },
    },
    {
      title: <Typography noWrap>Action</Typography>,
      dataIndex: "writeback_mode",
      key: "writeback_mode",
      showSorterTooltip: false,
      width: 100,
      render: (mode: string) => mode ?? "-",
    },
    {
      title: <Typography noWrap>Field Mapping</Typography>,
      dataIndex: "field_mappings",
      key: "field_mappings",
      showSorterTooltip: false,
      width: 160,
      render: (_: unknown, run) => {
        const mappings = run.field_mappings;
        if (!mappings || mappings.length === 0) {
          return "-";
        }

        return (
          <Tooltip
            variant="light"
            placement="bottomLeft"
            overlayStyle={{ maxWidth: "none" }}
            overlayInnerStyle={{ padding: "12px 16px" }}
            title={
              <table style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "4px 12px",
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#888",
                        borderBottom: "1px solid #e8e8e8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Sheet Column
                    </th>
                    <th style={{ borderBottom: "1px solid #e8e8e8", padding: "4px 12px" }} />
                    <th
                      style={{
                        textAlign: "left",
                        padding: "4px 0",
                        fontWeight: 600,
                        fontSize: 11,
                        color: "#888",
                        borderBottom: "1px solid #e8e8e8",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Destination Field
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m, i) => (
                    <tr key={i}>
                      <td style={{ padding: "4px 12px", whiteSpace: "nowrap" }}>
                        {m.ss_column_name}
                      </td>
                      <td style={{ padding: "4px 12px", color: "#999" }}>→</td>
                      <td style={{ padding: "4px 0", whiteSpace: "nowrap" }}>
                        <span
                          style={{
                            backgroundColor: "#F5F5F5",
                            borderRadius: 4,
                            padding: "2px 8px",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {m.source_field_name}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <span
              style={{
                borderBottom: "1px dashed currentColor",
                cursor: "default",
              }}
            >
              {mappings.length} mapped field{mappings.length !== 1 ? "s" : ""}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: <Typography noWrap>Exported At</Typography>,
      dataIndex: "created_dt",
      key: "created_dt",
      showSorterTooltip: false,
      width: 160,
      filterDropdown: ({ confirm }) => (
        <SortDropdown confirm={confirm} columnKey="created_dt" setSortedColumn={setSortedColumn} />
      ),
      filterIcon: () => getSortIcon(sortedColumn, "created_dt"),
      render: (_, run) => {
        return (
          <Tooltip
            title={
              run.error_description ? (
                <FlexColumn>
                  <span style={{ fontWeight: "700" }}>Error: </span>
                  {run.error_description}
                </FlexColumn>
              ) : undefined
            }
          >
            <div style={{ display: "inline-block" }}>
              <StatusTag
                // Keep in sync with spec.WritebackRunOverallStatusType (coeff/server/spec.py)
                status={
                  run.run_status_type === "ok"
                    ? "ok"
                    : run.run_status_type === "some_records_failed"
                    ? "warning"
                    : "failed"
                }
                label={moment(run.created_dt).format("MMM D, YYYY h:mma")}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: <Typography noWrap>Created By</Typography>,
      dataIndex: "created_by",
      key: "created_by",
      showSorterTooltip: false,
      width: 180,
      render: (createdBy: string, run) => (
        <Typography noWrap title={run.user_email} style={{ maxWidth: 180 }}>
          {createdBy}
        </Typography>
      ),
    },
    {
      title: <Typography noWrap>Duration</Typography>,
      dataIndex: "run_duration_sec",
      key: "run_duration_sec",
      showSorterTooltip: false,
      width: 120,
      render: (runDurationSec: number) => {
        if (runDurationSec < 0) {
          return "—";
        }
        return runDurationSec > 60
          ? `${Math.round(runDurationSec / 60)}m ${Math.round(runDurationSec % 60)}s`
          : `${Math.round(runDurationSec)}s`;
      },
    },
    {
      title: <Typography noWrap>Trigger</Typography>,
      dataIndex: "trigger_description",
      key: "trigger_description",
      showSorterTooltip: false,
      width: 200,
      render: (triggerDescription: string) => capitalize(triggerDescription),
    },
    {
      title: <Typography noWrap>Successful Rows</Typography>,
      dataIndex: "num_successful_rows",
      key: "num_successful_rows",
      showSorterTooltip: false,
      width: 130,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: <Typography noWrap>Failed Rows</Typography>,
      dataIndex: "num_failed_rows",
      key: "num_failed_rows",
      showSorterTooltip: false,
      width: 110,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: <Typography noWrap>Skipped Rows</Typography>,
      dataIndex: "num_skipped_rows",
      key: "num_skipped_rows",
      showSorterTooltip: false,
      width: 120,
      render: (val: number) => val.toLocaleString(),
    },
    {
      title: <Typography noWrap>Full Log</Typography>,
      key: "full_log",
      showSorterTooltip: false,
      width: 110,
      render: (_: unknown, run: AdminExtWritebackRun) => (
        <a
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            downloadRunHistory(run.writeback_run_id);
          }}
        >
          Full log (CSV)
        </a>
      ),
    },
    {
      title: "",
      showSorterTooltip: false,
      width: 40,
      render: (_: unknown, run: AdminExtWritebackRun) => (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <IconDropdown
            dropdownProps={{
              overlay: (
                <Menu>
                  <Menu.Item
                    onClick={() => {
                      window.open(run.spreadsheet_url, "_blank");
                    }}
                  >
                    Open Sheet
                  </Menu.Item>
                </Menu>
              ),
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Container>
      <WritebackRunsFilterToolbar
        domain={domain}
        search={search}
        onSearchChange={value => setSearch(value)}
        filters={filters}
        setFilters={onFilterChange}
      />

      <FlexColumn gap={0.5} style={{ overflow: "hidden" }}>
        <AdminTable
          columns={columns}
          dataSource={writebackRuns}
          loading={false}
          locale={{
            emptyText: isLoading ? (
              <LoaderWithPerfTimings name="WritebackRuns.locale" />
            ) : (
              "No export runs"
            ),
          }}
          rowKey={r => r.writeback_run_id ?? ""}
          pagination={false}
        />

        <FlexRow style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
          {writebackRuns.length > 0 && (
            <Typography color="textSecondary">
              Showing {pageOffset + 1}–{pageOffset + writebackRuns.length} runs (last 30 days)
            </Typography>
          )}

          <FlexRow gap={0.5}>
            <PaginationButton
              type="text"
              size="small"
              disabled={page === 0}
              onClick={() => {
                setPageAndOffset(page - 1);
              }}
            >
              <ChevronLeft style={{ height: 11 }} />
            </PaginationButton>

            <PaginationButton
              type="text"
              size="small"
              disabled={!hasMore}
              onClick={() => {
                setPageAndOffset(page + 1);
              }}
            >
              <ChevronLeft style={{ height: 11, transform: "rotate(180deg)" }} />
            </PaginationButton>
          </FlexRow>
        </FlexRow>
      </FlexColumn>
    </Container>
  );
};
